import IconWarning from '@apollo/icons/default/IconWarning.svg';
import {
  Group,
  Link,
  Table,
  TableSortButton,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import classnames from 'classnames';
import { orderBy } from 'lodash';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { MetricInfoTooltip } from 'src/app/graph/insights/fieldPage/usage/fieldPageClientMetrics/fieldPageClientPanel/MetricInfoTooltip';
import { OperationIdBadge } from 'src/app/graph/operationsPage/OperationIdBadge';
import { fieldRouteConfig, insightsRouteConfig } from 'src/app/graph/routes';
import { MutationBadge } from 'src/components/common/operationTypeBadge/MutationBadge';
import { SubscriptionBadge } from 'src/components/common/operationTypeBadge/SubscriptionBadge';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { format } from 'src/lib/format';
import {
  MUTATION_OPERATION_TYPE,
  SUBSCRIPTION_OPERATION_TYPE,
} from 'src/lib/metrics';

import {
  ClientMetrics,
  OperationMetrics,
  OperationName,
} from '../../../useCurrentField';

const { max } = Math;

interface ClientMetricsObject extends OperationMetrics {
  queryName: OperationName;
  queryNameIgnoreCase: OperationName;
}

type Direction = 'desc' | 'asc';
type Column = keyof ClientMetricsObject;

interface Props {
  clientMetrics: ClientMetrics;
  canViewClientVersions: boolean;
}

const OperationTypeWithTooltip = memo(
  ({ opTypes }: { opTypes: Set<string> }) => {
    if (opTypes.size === 1 && opTypes.has(MUTATION_OPERATION_TYPE)) {
      return <MutationBadge />;
    } else if (opTypes.size === 1 && opTypes.has(SUBSCRIPTION_OPERATION_TYPE)) {
      return <SubscriptionBadge />;
    }
    return null;
  },
);

const ExecutionCountWithTooltip = memo(
  ({
    estimatedExecutions,
    executions,
    opTypes,
  }: {
    estimatedExecutions: number;
    executions: number;
    opTypes: Set<string>;
  }) => {
    const showExecutionEstimate =
      estimatedExecutions && estimatedExecutions !== executions;
    const showSubscriptions = opTypes.has(SUBSCRIPTION_OPERATION_TYPE);
    return (
      <Group gap={1}>
        {showSubscriptions && (
          <Tooltip
            openDelay={200}
            label="This execution count does not yet include any field
          executions for subscription operations."
            data-testid="executions-subscriptions-tooltip"
          >
            <IconWarning className="text-icon-warning" />
          </Tooltip>
        )}
        <Tooltip
          openDelay={200}
          disabled={!showExecutionEstimate}
          placement="bottom-end"
          label={
            <div data-testid="executions-tooltip">
              <span>
                This number is an estimate based on sampling in your GraphQL
                server. {format.metric(executions, '0[.]0a')} field{' '}
                {executions === 1 ? 'execution was' : 'executions were'}{' '}
                observed during operations with field-level instrumentation
                enabled.
              </span>
            </div>
          }
        >
          <span>
            {format.number(
              max(estimatedExecutions ?? 0, executions ?? 0),
              '0[.]0a',
            )}
          </span>
        </Tooltip>
      </Group>
    );
  },
);

export const FieldPageClientPanel = ({
  clientMetrics,
  canViewClientVersions,
}: Props) => {
  const { graphId, graphVariant, typeName, fieldName, insightsTab } =
    useRouteParams(fieldRouteConfig);

  let previousCoordinate = {};
  switch (insightsTab) {
    case 'fields':
    case 'objectFields':
      previousCoordinate = {
        previousFieldType: typeName,
        previousFieldName: fieldName,
      };
      break;
    case 'inputFields':
      previousCoordinate = {
        previousInputFieldNamedType: typeName,
        previousInputFieldNamedAttribute: fieldName,
      };
      break;
    case 'enumValues':
      previousCoordinate = {
        previousEnumNamedType: typeName,
        previousEnumNamedAttribute: fieldName,
      };
      break;
    default:
      break;
  }

  const useExecutionMetrics =
    clientMetrics.requests === 0 && clientMetrics.executions > 0;
  const [order, setOrder] = useState<Column>(
    useExecutionMetrics ? 'executions' : 'requests',
  );
  const [dir, setDir] = useState<Direction>('desc');
  const location = useLocation();

  const data = useMemo<ClientMetricsObject[]>(
    () =>
      orderBy(
        Array.from(clientMetrics.operationMetrics.entries()).map(
          ([queryId, operationMetrics]) => ({
            ...operationMetrics,
            queryNameIgnoreCase: operationMetrics.queryName.toLocaleLowerCase(),
            queryId,
          }),
        ),
        [order],
        [dir],
      ),
    [clientMetrics, order, dir],
  );

  const updateSort = useCallback(
    (column: Column) => {
      if (order === column) {
        setDir(dir === 'asc' ? 'desc' : 'asc');
      } else {
        setOrder(column);
        // Numeric columns get sorted in descending order by default.
        setDir(column === 'queryNameIgnoreCase' ? 'asc' : 'desc');
      }
    },
    [order, setOrder, dir, setDir],
  );

  return (
    <Table
      size="condensed"
      sortBy={order}
      sortOrder={dir}
      onRequestSort={({ sortBy }) => updateSort(sortBy)}
    >
      <Thead>
        <Tr>
          <Th className="bg-secondary">
            <Group gap={2}>
              Operation
              <TableSortButton attribute="queryNameIgnoreCase" />
            </Group>
          </Th>
          {canViewClientVersions && (
            <Th isNumeric className="bg-secondary">
              <Group gap={2} className="justify-end">
                <TableSortButton attribute="versionCount" />
                Client versions
              </Group>
            </Th>
          )}
          {clientMetrics.requests > 0 && (
            <Th isNumeric className="bg-secondary">
              <Group gap={2}>
                <TableSortButton attribute="requests" />
                <MetricInfoTooltip type="referencingOperationCount" />
                Requests
              </Group>
            </Th>
          )}
          {clientMetrics.executions > 0 && (
            <Th isNumeric className="bg-secondary">
              <Group gap={2}>
                <TableSortButton attribute="executions" />
                <MetricInfoTooltip type="executionCount" />
                Executions
              </Group>
            </Th>
          )}
        </Tr>
      </Thead>
      <Tbody>
        {data.map((metrics) => {
          const {
            allOperationTypes,
            estimatedExecutions,
            executions,
            queryId,
            queryName,
            requests,
            versionCount,
          } = metrics;

          return (
            <Tr key={queryId}>
              <Td>
                <Group gap={1}>
                  <Link
                    className={classnames('truncate', { italic: !queryName })}
                    title={queryName}
                    to={insightsRouteConfig.locationFrom({
                      location,
                      patch: {
                        graphVariant,
                        queryName,
                        query: queryId,
                        graphId,
                        insightsTab: 'operations',
                        ...previousCoordinate,
                      },
                      fromRouteConfig: fieldRouteConfig,
                    })}
                  >
                    {queryName || 'Unnamed query'}
                  </Link>
                  <OperationTypeWithTooltip opTypes={allOperationTypes} />
                  <OperationIdBadge id={queryId} />
                </Group>
              </Td>
              {canViewClientVersions && (
                <Td isNumeric>
                  <Tooltip
                    openDelay={200}
                    label={`${versionCount} ${format.pluralize(
                      versionCount,
                      'version',
                    )}`}
                  >
                    <span>{versionCount}</span>
                  </Tooltip>
                </Td>
              )}
              {clientMetrics.requests > 0 && (
                <Td isNumeric>
                  <Tooltip
                    openDelay={200}
                    label={`${format.number(
                      requests,
                      '0,0',
                    )} ${format.pluralize(requests, 'request')}`}
                  >
                    <span>{format.number(requests, '0[.]0a')}</span>
                  </Tooltip>
                </Td>
              )}
              {clientMetrics.executions > 0 && (
                <Td isNumeric>
                  <ExecutionCountWithTooltip
                    executions={executions}
                    estimatedExecutions={estimatedExecutions}
                    opTypes={allOperationTypes}
                  />
                </Td>
              )}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
};
